import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { X, Trash2, Check } from 'lucide-react';

interface SignatureModalProps {
  isOpen: boolean;
  onConfirm: (signatureDataUrl: string) => Promise<void>;
  onClose: () => void;
}

export const SignatureModal: React.FC<SignatureModalProps> = ({
  isOpen,
  onConfirm,
  onClose
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Set canvas size based on parent container
        const parent = canvas.parentElement;
        if (parent) {
          canvas.width = parent.clientWidth;
          canvas.height = parent.clientHeight;
        }

        // Set drawing style
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Clear canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [isOpen]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    setIsEmpty(false);

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const handleConfirm = async () => {
    if (isEmpty) {
      alert(t('warehouse.modules.picking.signature.drawFirst'));
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      setIsSubmitting(true);
      const dataUrl = canvas.toDataURL('image/png');
      await onConfirm(dataUrl);
      onClose();
    } catch (error) {
      console.error('Error saving signature:', error);
      alert(t('warehouse.modules.picking.signature.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold">{t('warehouse.modules.picking.signature.title')}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        {/* Canvas */}
        <div className="flex-1 p-4 sm:p-6 flex flex-col">
          <div className="border-2 border-gray-300 rounded-lg bg-white flex-1 min-h-[300px] sm:min-h-0">
            <canvas
              ref={canvasRef}
              className="w-full h-full cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {t('warehouse.modules.picking.signature.instruction')}
          </p>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-4 sm:p-6 border-t border-gray-200 gap-3">
          <button
            onClick={clearSignature}
            className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 min-h-[44px] border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            <Trash2 size={18} />
            {t('common.buttons.delete')}
          </button>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 sm:py-2 min-h-[44px] border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors order-2 sm:order-1"
              disabled={isSubmitting}
            >
              {t('common.buttons.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              className="flex items-center justify-center gap-2 px-6 py-3 sm:py-2 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed order-1 sm:order-2"
              disabled={isSubmitting || isEmpty}
            >
              <Check size={18} />
              {isSubmitting ? t('common.status.saving') : t('common.buttons.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
